import { useCallback, useEffect, useMemo, useState } from 'react';
import { AdminAction } from '../components/PermissionGate';
import { createQaTest, deleteQaTest, fetchQaOptions, fetchQaTests, updateQaTest } from './api';
import { AppConfigEditor } from './AppConfigEditor';
import { ConfirmDialog } from './ConfirmDialog';
import { formatTimestamp } from './format';
import type {
  QaApp,
  QaAppConfigDraft,
  QaOptions,
  QaTestDefinition,
} from './types';
import { QA_APPS } from './types';

type Drafts = Record<QaApp, QaAppConfigDraft>;

function emptyDrafts(options: QaOptions): Drafts {
  const { defaults } = options;
  const base = (app: QaApp): QaAppConfigDraft => ({
    enabled: false,
    count: defaults.count,
    burst_size: defaults.burst_size,
    burst_delay_seconds: defaults.burst_delay_seconds,
    // Matches the backend default: a stress test that spends money must be
    // asked for explicitly.
    dummy: true,
    dummy_image_delay: null,
    dummy_other_delay: null,
    workloads: [],
    scanIds: [],
    ...(app === 'genapi' ? { model_type: options.default_model_type } : {}),
  });
  return Object.fromEntries(QA_APPS.map((app) => [app, base(app)])) as Drafts;
}

function draftsFromDefinition(definition: QaTestDefinition, options: QaOptions): Drafts {
  const drafts = emptyDrafts(options);
  for (const app of QA_APPS) {
    const saved = definition.per_app_config?.[app];
    if (!saved) continue;
    drafts[app] = {
      ...drafts[app],
      enabled: Boolean(saved.enabled),
      count: saved.count ?? drafts[app].count,
      burst_size: saved.burst_size ?? drafts[app].burst_size,
      burst_delay_seconds: saved.burst_delay_seconds ?? drafts[app].burst_delay_seconds,
      dummy: saved.dummy ?? true,
      dummy_image_delay: saved.dummy_image_delay ?? null,
      dummy_other_delay: saved.dummy_other_delay ?? null,
      model_type: saved.model_type ?? drafts[app].model_type,
      workloads: saved.workloads ?? [],
      scanIds: definition.scan_ids_by_app?.[app] ?? [],
    };
  }
  return drafts;
}

/**
 * Tests Bank: the saved definitions and the editor that produces them.
 *
 * Definitions are append-only server-side, so "save" on an existing test creates
 * version N+1 rather than mutating it — a run in the history can always be traced
 * back to the exact configuration that produced it.
 */
export function TestsBankPage() {
  const [options, setOptions] = useState<QaOptions | null>(null);
  const [definitions, setDefinitions] = useState<QaTestDefinition[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState<QaTestDefinition | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [drafts, setDrafts] = useState<Drafts | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<QaTestDefinition | null>(null);
  const [deleting, setDeleting] = useState(false);

  const reload = useCallback(async () => {
    try {
      setDefinitions(await fetchQaTests());
      setLoadError(null);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [opts, tests] = await Promise.all([fetchQaOptions(), fetchQaTests()]);
        if (cancelled) return;
        setOptions(opts);
        setDefinitions(tests);
        setLoadError(null);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openNew = useCallback(() => {
    if (!options) return;
    setEditing(null);
    setName('');
    setDescription('');
    setDrafts(emptyDrafts(options));
    setSaveError(null);
    setSavedNotice(null);
    setFormOpen(true);
  }, [options]);

  const openEdit = useCallback(
    (definition: QaTestDefinition) => {
      if (!options) return;
      setEditing(definition);
      setName(definition.name);
      setDescription(definition.description || '');
      setDrafts(draftsFromDefinition(definition, options));
      setSaveError(null);
      setSavedNotice(null);
      setFormOpen(true);
    },
    [options],
  );

  const patchDraft = useCallback((app: QaApp, patch: Partial<QaAppConfigDraft>) => {
    setDrafts((current) => (current ? { ...current, [app]: { ...current[app], ...patch } } : current));
  }, []);

  const enabledApps = useMemo(
    () => (drafts ? QA_APPS.filter((app) => drafts[app].enabled) : []),
    [drafts],
  );
  const realSpendApps = useMemo(
    () =>
      drafts
        ? enabledApps.filter((app) => app !== 'served_models' && !drafts[app].dummy)
        : [],
    [drafts, enabledApps],
  );

  async function save() {
    if (!drafts) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setSaveError('A test needs a name so its runs can be grouped in the history.');
      return;
    }
    if (enabledApps.length === 0) {
      setSaveError('Enable at least one application, otherwise there is nothing to run.');
      return;
    }
    if (drafts.served_models.enabled && drafts.served_models.workloads.length === 0) {
      setSaveError('Add at least one model workload before enabling self-served models.');
      return;
    }
    if (
      drafts.served_models.enabled &&
      drafts.served_models.workloads.some((workload) => !workload.image_url.trim())
    ) {
      setSaveError('Every self-served model workload needs an image URL.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const perApp: Record<string, unknown> = {};
      const scanIds: Record<string, string[]> = {};
      for (const app of enabledApps) {
        const draft = drafts[app];
        if (app === 'served_models') {
          perApp[app] = {
            enabled: true,
            workloads: draft.workloads,
          };
          continue;
        }
        perApp[app] = {
          enabled: true,
          burst_delay_seconds: draft.burst_delay_seconds,
          dummy: draft.dummy,
          dummy_image_delay: draft.dummy_image_delay,
          dummy_other_delay: draft.dummy_other_delay,
          // Only gen-api is told how much load to apply. Everywhere else the CSV
          // says so, and saving a count that nothing reads would just be a
          // number waiting to be believed.
          ...(app === 'genapi'
            ? {
                count: draft.count,
                burst_size: draft.burst_size,
                model_type: draft.model_type,
              }
            : {}),
        };
        if (app !== 'genapi' && draft.scanIds.length > 0) scanIds[app] = draft.scanIds;
      }

      const payload = {
        name: trimmed,
        description: description.trim() || null,
        per_app_config: perApp,
        scan_ids_by_app: scanIds,
      };
      const saved = editing
        ? await updateQaTest(editing.id, payload)
        : await createQaTest(payload);

      setFormOpen(false);
      setSavedNotice(
        saved.version > 1
          ? `Saved "${saved.name}" as version ${saved.version}.`
          : `Saved "${saved.name}".`,
      );
      await reload();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setLoadError(null);
    try {
      const result = await deleteQaTest(pendingDelete.id);
      setPendingDelete(null);
      setSavedNotice(
        result.versions_deleted > 1
          ? `Deleted "${result.name}" and its ${result.versions_deleted} saved versions.`
          : `Deleted "${result.name}".`,
      );
      // An open editor for the test that just went would save it straight back.
      if (editing?.name === result.name) setFormOpen(false);
      await reload();
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return <div className="p-6 text-sm text-slate-500">Loading tests…</div>;
  }

  if (!options) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm">
          {loadError || 'Could not load QA configuration.'}
        </div>
      </div>
    );
  }

  const optionByKey = new Map(options.applications.map((a) => [a.key, a]));

  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title="Delete test"
        message={
          pendingDelete
            ? `Are you sure you want to delete "${pendingDelete.name}"? ` +
              `All ${pendingDelete.version} saved version${pendingDelete.version === 1 ? '' : 's'} ` +
              'will be removed and it can no longer be run. Runs already in the history are kept.'
            : ''
        }
        confirmLabel="Delete test"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        isLoading={deleting}
      />

      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tests bank</h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure how much load each application takes, then run the test from the Run test tab.
            Saving an existing test adds a version rather than replacing it.
          </p>
        </div>
        <AdminAction>
          <button
            onClick={openNew}
            className="shrink-0 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            New test
          </button>
        </AdminAction>
      </div>

      {savedNotice && (
        <div className="rounded-xl border border-success-200 bg-success-50 text-success-700 px-3 py-2 text-sm">
          {savedNotice}
        </div>
      )}
      {loadError && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-3 py-2 text-sm">
          {loadError}
        </div>
      )}

      {formOpen && drafts && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5">
          <h2 className="text-base font-semibold text-slate-900">
            {editing ? `Edit "${editing.name}"` : 'New test'}
          </h2>
          {editing && (
            <p className="text-xs text-slate-500 mt-1">
              Editing version {editing.version}. Saving creates version {editing.version + 1} and
              leaves earlier runs traceable to what they actually ran.
            </p>
          )}

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. peak-hour, all applications"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-200"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Description (optional)</span>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this test is meant to prove"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-200"
              />
            </label>
          </div>

          <div className="mt-5 space-y-3">
            {QA_APPS.map((app) => {
              const option = optionByKey.get(app);
              if (!option) return null;
              return (
                <AppConfigEditor
                  key={app}
                  app={app}
                  option={option}
                  options={options}
                  draft={drafts[app]}
                  onChange={(patch) => patchDraft(app, patch)}
                />
              );
            })}
          </div>

          {realSpendApps.length > 0 && (
            <div className="mt-4 rounded-xl border border-warning-200 bg-warning-50 text-warning-700 px-3 py-2 text-xs">
              Dummy mode is off for{' '}
              {realSpendApps.map((app) => optionByKey.get(app)?.label || app).join(', ')}. Running
              this test will spend real money on generations.
            </div>
          )}

          {saveError && (
            <div className="mt-4 rounded-lg border border-danger/30 bg-danger/5 text-danger px-3 py-2 text-sm">
              {saveError}
            </div>
          )}

          <div className="mt-5 flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              className="px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving…' : editing ? 'Save as new version' : 'Save test'}
            </button>
            <button
              onClick={() => setFormOpen(false)}
              className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-soft overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-900">Saved tests</h2>
        </div>
        {definitions.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500 text-center">
            No tests yet. Create one to get started.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-slate-500 border-b border-slate-100">
                <th className="px-5 py-2.5">Version</th>
                <th className="px-5 py-2.5">Name</th>
                <th className="px-5 py-2.5">Applications</th>
                <th className="px-5 py-2.5">Load</th>
                <th className="px-5 py-2.5">Saved</th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {definitions.map((definition) => {
                const apps = QA_APPS.filter((app) => definition.per_app_config?.[app]?.enabled);
                // A scan application's load is its CSV; only gen-api, which has
                // no CSV, is told how many calls to make.
                const totalUnits = apps.reduce(
                  (sum, app) =>
                    sum +
                    (app === 'genapi'
                      ? definition.per_app_config?.genapi?.count || 0
                      : app === 'served_models'
                        ? (definition.per_app_config?.served_models?.workloads || []).reduce(
                            (total, workload) => total + workload.count,
                            0,
                          )
                      : definition.scan_ids_by_app?.[app]?.length || 0),
                  0,
                );
                const anyReal = apps.some(
                  (app) => app !== 'served_models' && !definition.per_app_config?.[app]?.dummy,
                );
                return (
                  <tr key={definition.id} className="border-b border-slate-50 last:border-0">
                    <td className="px-5 py-3 font-mono text-xs text-slate-500">
                      v{definition.version}
                    </td>
                    <td className="px-5 py-3">
                      <span className="font-medium text-slate-900">{definition.name}</span>
                      {definition.description && (
                        <span className="block text-xs text-slate-500">
                          {definition.description}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-600">
                      {apps.map((app) => optionByKey.get(app)?.label || app).join(', ') || '—'}
                    </td>
                    <td className="px-5 py-3 text-slate-600">
                      {totalUnits.toLocaleString()} units
                      {anyReal && (
                        <span className="ml-2 rounded-full bg-warning-50 px-2 py-0.5 text-[11px] font-medium text-warning-700">
                          Real spend
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-500">
                      {formatTimestamp(definition.created_at)}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <AdminAction>
                        <button
                          onClick={() => openEdit(definition)}
                          className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setPendingDelete(definition)}
                          className="ml-3 text-xs text-danger-700 hover:text-danger-600 transition-colors"
                        >
                          Delete
                        </button>
                      </AdminAction>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
